import React, { useEffect, useState } from "react";
import { useHistory } from "react-router-dom";
import { post } from "../lib/api";
import { channelLabel, formatDay } from "../lib/format";
import { CURRENCIES, LEAD_SOURCES, OPEN_STAGES, leadError, signalTypeLabel, stageLabel } from "../lib/leads";
import { todayISO } from "../../helpers/datetime";
import { Button, Field, Input, Modal, Select, Spinner, Textarea } from "../ui";
import EntityPicker from "../components/EntityPicker";
import NextStepFields from "../components/NextStepFields";
import { useShell } from "./context";

const blank = (p = {}) => {
    const touch = p.touch;
    const signal = p.signal;
    let company = p.company || null;
    let contact = p.contact || null;
    if (touch) {
        if (touch.company_id) company = { id: touch.company_id, name: touch.company_name || "Company" };
        if (touch.contact_id) contact = { id: touch.contact_id, name: touch.contact_name || "Contact" };
    }
    if (signal) company = { id: signal.company_id, name: signal.company_name };
    if (!company && contact && contact.current_role && contact.current_role.company_id) {
        company = { id: contact.current_role.company_id, name: contact.current_role.company_name };
    }
    return {
        title: p.title || "",
        company,
        contact,
        introducer: null,
        source: signal ? "signal" : "",
        stage: "spotted",
        est_value: "",
        currency: "EUR",
        fx_rate_to_eur: "",
        next_step: "",
        next_step_date: "",
        notes: "",
    };
};

// "New lead": from the Leads page, a company or person, a touch (origin
// touch) or a signal (POST /v1/crm/signals/:id/lead, which also marks the
// signal acted on). An open lead needs a next step and a date (API 422).
export default function LeadModal() {
    const { leadState, closeLead, toast, bump } = useShell();
    const history = useHistory();
    const prefill = (leadState && leadState.prefill) || {};
    const [f, setF] = useState(blank());
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState(null);

    useEffect(() => {
        if (!leadState) return;
        setF(blank(leadState.prefill));
        setError(null);
        setBusy(false);
    }, [leadState]);

    if (!leadState) return null;
    const set = (k) => (v) => setF((s) => ({ ...s, [k]: v && v.target ? v.target.value : v }));
    const { touch, signal } = prefill;
    const companyFromTouch = touch && !f.company;

    const save = async () => {
        if (!f.company && !touch) return setError({ message: "Pick the company the lead is at.", field: "company" });
        if (!f.next_step.trim() || !f.next_step_date) return setError({ message: "An open lead needs a next step and a date for it.", field: "next_step" });
        if (f.est_value !== "" && !(Number(f.est_value) >= 0)) return setError({ message: "The value must be a positive number.", field: "value" });
        setError(null);
        setBusy(true);
        const body = {
            title: f.title.trim(),
            stage: f.stage,
            next_step: f.next_step.trim(),
            next_step_date: f.next_step_date,
            notes: f.notes.trim(),
        };
        if (f.company) body.company_id = f.company.id;
        if (f.contact) body.primary_contact_id = f.contact.id;
        if (f.introducer) body.introduced_by_contact_id = f.introducer.id;
        if (f.source) body.source = f.source;
        if (f.est_value !== "") {
            body.est_value = Number(f.est_value);
            body.currency = f.currency;
            if (f.currency !== "EUR" && f.fx_rate_to_eur !== "") body.fx_rate_to_eur = Number(f.fx_rate_to_eur);
        }
        if (touch) body.origin_touch_id = touch.id;
        Object.keys(body).forEach((k) => body[k] === "" && delete body[k]);
        try {
            const lead = signal ? await post(`v1/crm/signals/${signal.id}/lead`, body) : await post("v1/crm/leads", body);
            toast(signal ? "Lead created; the signal is marked acted on." : "Lead created.", { icon: "ri-funds-line" });
            bump();
            closeLead();
            if (prefill.onCreated) prefill.onCreated(lead);
            if (!prefill.stay) history.push(`/crm/leads/${lead.id}`);
        } catch (e) {
            setError(leadError(e));
            setBusy(false);
        }
    };

    const origin = touch ? (
        <div className="hz-alert hz-alert-info hz-small">
            <i className="ri-chat-history-line"></i> Started from a touch: {channelLabel(touch.channel)}
            {touch.contact_name ? ` with ${touch.contact_name}` : ""} on {formatDay(touch.touch_date, { short: true })}
            {touch.summary ? `: ${touch.summary}` : ""}
        </div>
    ) : signal ? (
        <div className="hz-alert hz-alert-info hz-small">
            <i className="ri-radar-line"></i> Started from a signal: {signalTypeLabel(signal.type)} at {signal.company_name}, seen {formatDay(signal.seen_on, { short: true })}
            {signal.detail ? `: ${signal.detail}` : ""}
        </div>
    ) : null;

    return (
        <Modal
            open
            title="New lead"
            onClose={closeLead}
            width={600}
            footer={
                <>
                    <Button variant="light" onClick={closeLead}>Cancel</Button>
                    <Button variant="primary" onClick={save} disabled={busy}>
                        {busy && <Spinner />}Create lead
                    </Button>
                </>
            }
        >
            <div className="hz-form-grid" onKeyDown={(e) => (e.metaKey || e.ctrlKey) && e.key === "Enter" && save()}>
                {origin}
                {error && <div className="hz-alert hz-alert-danger">{error.message}</div>}
                <div className="hz-form-2">
                    <div>
                        <span className="hz-label">Company{!touch && <span className="req"> *</span>}</span>
                        {signal ? (
                            <span className="hz-picked"><i className="ri-building-line"></i><span className="hz-ellipsis">{signal.company_name}</span></span>
                        ) : (
                            <EntityPicker kind="company" value={f.company} onChange={set("company")} autoFocus={!f.company && !touch} />
                        )}
                        {companyFromTouch && <div className="hz-hint">Empty: taken from the touch or the person's current company.</div>}
                    </div>
                    <Field label="Title" hint="Tells several leads at one company apart">
                        <Input value={f.title} onChange={set("title")} placeholder="Core banking rescue" autoFocus={!!f.company || !!touch} />
                    </Field>
                </div>
                <div className="hz-form-2">
                    <div>
                        <span className="hz-label">Primary contact</span>
                        <EntityPicker kind="contact" value={f.contact} onChange={set("contact")} />
                    </div>
                    <Field label="Stage">
                        <Select value={f.stage} onChange={set("stage")} options={OPEN_STAGES.map((s) => ({ value: s, label: stageLabel(s) }))} />
                    </Field>
                </div>
                <div className="hz-form-2">
                    <Field label="Source" hint={signal ? "From a signal" : "Empty: introduction when someone introduced it, else direct"}>
                        <Select value={f.source} onChange={set("source")} placeholder="Automatic" options={LEAD_SOURCES} disabled={!!signal} />
                    </Field>
                    <div>
                        <span className="hz-label">Introduced by</span>
                        <EntityPicker kind="contact" value={f.introducer} onChange={set("introducer")} placeholder="Who made the intro" exclude={f.contact ? f.contact.id : undefined} />
                    </div>
                </div>
                <ValueFields f={f} set={set} invalid={error && error.field === "value"} />
                <NextStepFields
                    step={f.next_step}
                    date={f.next_step_date}
                    onStep={set("next_step")}
                    onDate={set("next_step_date")}
                    invalid={error && error.field === "next_step"}
                    from={todayISO()}
                />
                <Field label="Notes">
                    <Textarea rows={2} value={f.notes} onChange={set("notes")} />
                </Field>
            </div>
        </Modal>
    );
}

// Estimated value, currency and (for non-EUR) the rate to EUR.
export function ValueFields({ f, set, invalid }) {
    const options = CURRENCIES.includes(f.currency) ? CURRENCIES : [...CURRENCIES, f.currency];
    return (
        <div className="hz-form-2" style={{ gridTemplateColumns: f.currency !== "EUR" ? "1fr 100px 1fr" : "1fr 100px" }}>
            <Field label="Estimated value">
                <Input type="number" min={0} step="any" value={f.est_value} onChange={set("est_value")} placeholder="0" className={invalid ? "hz-invalid" : ""} />
            </Field>
            <Field label="Currency">
                <Select value={f.currency} onChange={set("currency")} options={options} />
            </Field>
            {f.currency !== "EUR" && (
                <Field label={`1 ${f.currency} in EUR`} hint="Empty: the rate from settings, if one is set">
                    <Input type="number" min={0} step="any" value={f.fx_rate_to_eur} onChange={set("fx_rate_to_eur")} placeholder="e.g. 0.92" />
                </Field>
            )}
        </div>
    );
}
